import { z } from "zod";

export const assessmentScopeSchema = z.enum(["topic", "route", "skill"]);
export type AssessmentScope = z.infer<typeof assessmentScopeSchema>;

const id = z.string().min(1).max(160);
export const knowledgeStatusSchema = z.enum(["mastered", "vulnerable", "learning_point", "blind_spot"]);
export const confidenceSchema = z.enum(["certain", "doubtful"]);
export const assessmentRulesSchema = z
  .object({
    version: id,
    correctThreshold: z.number().min(0).max(1),
    doubtThreshold: z.number().min(0).max(1),
    blindSpotCount: z.number().int().min(1),
  })
  .strict();
export const assessmentElementSchema = z
  .object({
    id,
    requirementId: id,
    title: z.string().min(1),
  })
  .strict();
export const assessmentCoursePartSchema = z
  .object({
    id,
    groupId: id,
    groupTitle: z.string().min(1),
    title: z.string().min(1),
    resourceId: id.nullable(),
  })
  .strict();
export const assessmentItemSchema = z
  .object({
    id,
    version: id,
    status: z.enum(["concept", "validated", "retired"]),
    skillId: id,
    knowledgeIds: z.array(id).min(1),
    critical: z.boolean(),
    sourcePartIds: z.array(id).min(1),
    case: z.string(),
    prompt: z.string().min(1),
    options: z.array(z.string().min(1)).length(4),
    correctOption: z.number().int().min(0).max(3),
    explanation: z.string().min(1),
  })
  .strict();
export const knowledgeAssessmentDefinitionSchema = z
  .object({
    id,
    version: id,
    stampId: id,
    pageId: id,
    title: z.string().min(1),
    language: z.enum(["nl", "en"]),
    sourceUrl: z.string().url(),
    validationStatus: z.enum(["pending_domain_validation", "validated"]),
    sourceNotes: z.array(z.string().min(1)),
    estimatedMinutes: z.number().int().positive(),
    rules: assessmentRulesSchema,
    skills: z.array(assessmentElementSchema).min(1),
    knowledge: z.array(assessmentElementSchema).min(1),
    courseParts: z.array(assessmentCoursePartSchema).min(1),
    items: z.array(assessmentItemSchema).min(1),
  })
  .strict();
export const assessmentVersionSchema = z
  .object({
    revision: z.number().int().positive(),
    publishedAt: z.string().datetime(),
    definition: knowledgeAssessmentDefinitionSchema,
  })
  .strict();
export const assessmentDeliverySchema = assessmentVersionSchema.omit({ definition: true }).extend({
  definition: knowledgeAssessmentDefinitionSchema.omit({ items: true }).extend({
    items: z.array(
      assessmentItemSchema.pick({ id: true, skillId: true, case: true, prompt: true, options: true }),
    ),
  }),
});
export const assessmentSummarySchema = z
  .object({
    id,
    stampId: id,
    pageId: id,
    title: z.string().min(1),
    version: id,
    questionCount: z.number().int().positive(),
  })
  .strict();
export const assessmentAnswerSchema = z
  .object({
    itemId: id,
    choice: z.number().int().min(0).max(3),
    confidence: confidenceSchema,
    timeMs: z.number().int().nonnegative(),
  })
  .strict();
export const assessmentSubmissionSchema = z
  .object({
    attemptId: z.string().uuid(),
    scope: assessmentScopeSchema.optional(),
    skillIds: z.array(id).min(1).optional(),
    revision: z.number().int().positive(),
    answers: z.array(assessmentAnswerSchema).min(1),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.scope === "skill" && !value.skillIds?.length) {
      context.addIssue({
        code: "custom",
        path: ["skillIds"],
        message: "Skill-scoped submissions require skillIds.",
      });
    }
    if (value.scope !== "skill" && value.skillIds?.length) {
      context.addIssue({
        code: "custom",
        path: ["skillIds"],
        message: "skillIds are only valid for skill-scoped submissions.",
      });
    }
  });
/** Demo-only: create a perfect skill-scoped attempt without answering each question. */
export const assessmentDemoSkillPassSchema = z
  .object({
    attemptId: z.string().uuid(),
    revision: z.number().int().positive(),
    skillIds: z.array(id).min(1),
  })
  .strict();
export const assessmentElementResultSchema = assessmentElementSchema.extend({
  status: knowledgeStatusSchema,
  correct: z.number().int().nonnegative(),
  total: z.number().int().positive(),
  criticalError: z.boolean(),
  itemIds: z.array(id),
  learningPartIds: z.array(id),
});
export const assessmentScoringSchema = z
  .object({
    correct: z.number().int().nonnegative(),
    total: z.number().int().positive(),
    criticalWrongItemIds: z.array(id),
    answers: z.array(
      assessmentAnswerSchema.extend({
        correct: z.boolean(),
        outcome: knowledgeStatusSchema,
        critical: z.boolean(),
      }),
    ),
    skills: z.array(assessmentElementResultSchema),
    knowledge: z.array(assessmentElementResultSchema),
    learningPath: z.array(
      assessmentCoursePartSchema.extend({
        required: z.boolean(),
        tested: z.boolean(),
        triggerItemIds: z.array(id),
      }),
    ),
  })
  .strict();
export const assessmentResultSchema = assessmentScoringSchema.extend({
  scope: assessmentScopeSchema.default("topic"),
  skillIds: z.array(id).min(1).optional(),
  attemptId: id,
  employeeId: id,
  revision: z.number().int().positive(),
  completedAt: z.string().datetime().nullable(),
  example: z.boolean(),
  catalogueVersionId: id,
  millerLevel: z.literal("weet_hoe"),
  definition: knowledgeAssessmentDefinitionSchema,
});
export const assessmentPublicationSchema = z
  .object({
    expectedRevision: z.number().int().nonnegative(),
    definition: knowledgeAssessmentDefinitionSchema,
  })
  .strict();
export const assessmentTeamSchema = z
  .object({
    assessmentId: id,
    participants: z.number().int().nonnegative(),
    skills: z.array(
      assessmentElementSchema.extend({
        counts: z
          .object({
            mastered: z.number(),
            vulnerable: z.number(),
            learning_point: z.number(),
            blind_spot: z.number(),
          })
          .strict(),
      }),
    ),
  })
  .strict();

export type KnowledgeStatus = z.infer<typeof knowledgeStatusSchema>;
export type AssessmentRules = z.infer<typeof assessmentRulesSchema>;
export type AssessmentElement = z.infer<typeof assessmentElementSchema>;
export type KnowledgeAssessmentDefinition = z.infer<typeof knowledgeAssessmentDefinitionSchema>;
export type AssessmentVersion = z.infer<typeof assessmentVersionSchema>;
export type AssessmentDelivery = z.infer<typeof assessmentDeliverySchema>;
export type AssessmentAnswer = z.infer<typeof assessmentAnswerSchema>;
export type AssessmentSubmission = z.infer<typeof assessmentSubmissionSchema>;
export type AssessmentDemoSkillPass = z.infer<typeof assessmentDemoSkillPassSchema>;
export type AssessmentScoring = z.infer<typeof assessmentScoringSchema>;
export type AssessmentResult = z.infer<typeof assessmentResultSchema>;
export type AssessmentPublication = z.infer<typeof assessmentPublicationSchema>;
export type AssessmentTeam = z.infer<typeof assessmentTeamSchema>;
